import { clientConfig } from '@/config/clientConfig';
import { formatDateTime } from '@/shared/lib/date-utils';
import { formatMinutesCompact } from '@/shared/lib/duration-utils';
import { Badge } from '@/shared/ui/badge';
import { Calendar, Clock, FileText, Hash, Layers, MapPin, Tag, User } from 'lucide-react';
import { InfoRow } from './InfoRow';
import { JobPrioritySelect } from './JobPrioritySelect';

interface JobDetailsData {
  id?: string | number;
  client: string;
  address: string;
  priorityId?: number | null;
  priorityName?: string | null;
  assignee?: string;
  scheduledDate?: string;
  dueDate?: string;
  requiredBy?: string;
  estimatedHours?: number;
  duration?: number;
  createdDate?: string;
  description?: string;
  tags?: string[];
  category?: string;
  applicationReference?: string | null;
}

export function JobDetailsCard({ jobData }: { jobData: JobDetailsData }) {
  // duration arrives in minutes, normalised by the API (WRK-5451). The
  // compact formatter handles null/0/missing with an em-dash.
  const durationLabel = formatMinutesCompact(jobData.duration);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-950 flex items-center justify-center">
          <FileText className="w-5 h-5 text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Job Details</h3>
          <p className="text-xs text-muted-foreground">Core job information</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 text-sm">
          <InfoRow icon={Layers} label="Job Category" value={jobData.category ?? 'Not set'} />

          {clientConfig.features.showApplicationReference && jobData.applicationReference && (
            <InfoRow
              icon={Hash}
              label="Application Reference"
              value={jobData.applicationReference}
            />
          )}

          {/* Editable — jobs arrive from CORE without a priority and default to
              Unknown, so a planner needs to be able to set it (SC-6369).
              YW-only (CWL-165): brands without the flag keep the read-only
              priority badge on the StatusBar and get no drop-down here. */}
          {clientConfig.features.editableJobPriority && jobData.id != null && (
            <JobPrioritySelect
              jobId={Number(jobData.id)}
              priorityId={jobData.priorityId ?? null}
              priorityName={jobData.priorityName ?? null}
            />
          )}

          <InfoRow icon={MapPin} label="Site Address" value={jobData.address || 'Not set'} />

          {jobData.assignee && <InfoRow icon={User} label="Assigned To" value={jobData.assignee} />}
          {jobData.scheduledDate && (
            <InfoRow
              icon={Calendar}
              label="Scheduled"
              value={formatDateTime(jobData.scheduledDate)}
            />
          )}
          {(jobData.dueDate || jobData.requiredBy) && (
            <InfoRow
              icon={Calendar}
              label={jobData.requiredBy ? 'Required By' : 'Due Date'}
              value={
                jobData.requiredBy
                  ? new Date(jobData.requiredBy).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : new Date(jobData.dueDate!).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
              }
            />
          )}
          {jobData.estimatedHours && (
            <InfoRow icon={Clock} label="Estimated Hours" value={`${jobData.estimatedHours}h`} />
          )}

          <InfoRow icon={Clock} label="Duration" value={durationLabel} />

          {jobData.createdDate && (
            <InfoRow
              icon={Calendar}
              label="Created Date"
              value={new Date(jobData.createdDate).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            />
          )}
        </div>

        {jobData.description && (
          <div className="pt-4 border-t border-border">
            <div className="text-xs text-muted-foreground mb-2">Description</div>
            <p className="text-sm text-foreground">{jobData.description}</p>
          </div>
        )}

        {jobData.tags && jobData.tags.length > 0 && (
          <div className="pt-4 border-t border-border">
            <div className="text-xs text-muted-foreground mb-2">Tags</div>
            <div className="flex items-center gap-2 flex-wrap">
              {jobData.tags.map((tag: string, idx: number) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  <Tag className="w-3 h-3 mr-1" />
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
